import React, { FormEvent, useState } from 'react'
import cn, { Argument as ClassName } from 'classnames'
import styled from 'styled-components'
import * as yup from 'yup'
import { useNavigate } from 'react-router-dom'

import {
    MAX_FIRST_NAME_LENGTH,
    MAX_LAST_NAME_LENGTH,
    MAX_MOBILE_LENGTH,
    MIN_PASSWORD_LENGTH,
} from '../../../common/domain/account-rules'
import { formatValidationErrorData } from '../../../common/components/sign-in-form'
import { useAuthRegisterMutation } from '../../../common/redux/api/web-api'
import { getErrorMessageCode } from '../../../common/utils/getErrorMessageCode'
import { InputGroup, SInput } from '../../../lib/input-group'
import { SubmitButton } from '../../../lib/submit-button'
import { FormHeader } from '../../../lib/form-header'
import Text from '../../../ui/Text'
import { PasswordInput } from '../../../lib/password-input'
import useUpdateEffect from '../../../common/hooks/useUpdateEffect'
import { SNotification } from '../../../lib/landing-page-layout'
import { validateEmailFormat } from '../../../common/utils/form-validation/field-format-validation'
import useText from '../../../common/hooks/useText'

const ContentWrapper = styled.div`
    width: 750px;
    font-size: 16px;
    padding: 0 10px;
`

const Row = styled.div<{ short?: boolean }>`
    display: flex;
    width: ${({ short }) => (short ? '50%' : '100%')};
    gap: 40px;
    justify-content: space-between;

    @media screen and (max-width: 775px) {
        flex-direction: column;
        width: 100%;
        margin-bottom: 40px;
    }

    @media screen and (min-width: 776px) {
        & > *:first-child:nth-last-child(1) {
            padding-right: 20px;
        }
    }
`

const Spacer = styled.div`
    margin-bottom: 30px;
`

const AgreementContainer = styled.div`
    width: 70%;
    text-align: center;
    align-self: center;

    @media screen and (max-width: 775px) {
        width: 100%;
    }

    a {
        color: ${({ theme }) => theme.brandColors.CA_Blue};
    }
`
const Form = styled.form`
    display: flex;
    flex-direction: column;

    @media screen and (min-width: 776px) {
        gap: 40px;
    }
`

const ReturnButton = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    color: ${({ theme }) => theme.font.midDarkColor};

    &:hover {
        cursor: pointer;
    }
`

type FormDataType = {
    firstName: string
    lastName: string
    email: string
    mobile: string
    password: string
}

const initialFormData: FormDataType = {
    firstName: '',
    lastName: '',
    email: '',
    mobile: '',
    password: '',
}

export type SignUpFormProps = {
    className?: ClassName
    style?: React.CSSProperties
    setStatus: (status: 'signup' | 'success' | 'failed') => void
}

export function SignUpForm({ className, style, setStatus }: SignUpFormProps): React.ReactElement {
    const text = useText()
    const navigate = useNavigate()
    const [createUser, { isLoading: loading }] = useAuthRegisterMutation()
    const [formData, setFormData] = useState<FormDataType>(initialFormData)
    const [formErrors, setFormErrors] = useState<FormDataType>(initialFormData)
    const [signUpError, setSignUpError] = useState<string | undefined>(undefined)
    const [showPassword, toggleShowPassword] = useState<boolean>(false)

    const createUserSchema = () =>
        yup.object().shape({
            firstName: yup
                .string()
                .required(text('lblEnterFirstName'))
                .max(MAX_FIRST_NAME_LENGTH, text('lblMaxOfCharacters', { maxLength: MAX_FIRST_NAME_LENGTH })),
            lastName: yup
                .string()
                .required(text('lblEnterLastName'))
                .max(MAX_LAST_NAME_LENGTH, text('lblMaxOfCharacters', { maxLength: MAX_LAST_NAME_LENGTH })),
            mobile: yup
                .string()
                .required(text('lblEnterMobile'))
                .max(MAX_MOBILE_LENGTH, text('lblMaxOfCharacters', { maxLength: MAX_MOBILE_LENGTH })),
            email: yup
                .string()
                .email(text('lblInvalidEmail'))
                .required(text('lblEnterEmail'))
                .test('match', text('lblInvalidEmail'), (val) => validateEmailFormat(val ?? '')),
            password: yup
                .string()
                .min(MIN_PASSWORD_LENGTH, text('msgPasswordToShortSignup'))
                .required(text('msgPasswordToShortSignup')),
        })

    /** Remove error message when start typing */
    useUpdateEffect(() => {
        if (signUpError) {
            setSignUpError(undefined)
        }
    }, [formData])
    /*******************************************/

    const handleSubmit = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
        event.preventDefault()

        try {
            await createUserSchema().validate(formData, { abortEarly: false })
            setFormErrors(() => initialFormData)

            await createUser({
                chargePointUser: {
                    ...formData,
                    personalAccept: new Date().toISOString(),
                },
            }).unwrap()

            setStatus('success')
        } catch (error) {
            if (error instanceof yup.ValidationError) {
                setFormErrors(formatValidationErrorData<FormDataType>(error, initialFormData) || initialFormData)
                return
            }

            const errorMessageCode = getErrorMessageCode(error)

            if (errorMessageCode) {
                setSignUpError(text(errorMessageCode))
            } else {
                setStatus('failed')
            }
        }
    }

    return (
        <ContentWrapper className={cn(className)} style={style}>
            <Spacer>
                {signUpError && (
                    <SNotification data-testid="notificationError" error>
                        {signUpError}
                    </SNotification>
                )}
            </Spacer>
            <Spacer>
                <FormHeader data-testid="header" />
            </Spacer>
            <Form onSubmit={handleSubmit} noValidate autoComplete="off">
                <Row>
                    <InputGroup
                        style={{ width: '100%', height: '40px' }}
                        label={formData.firstName.length > 0 ? text('lblFirstName') : ''}
                        errorTestId="signUpFirstNameErrorMsg"
                        errorMsg={formErrors.firstName}
                        inverted={false}
                    >
                        <SInput
                            data-testid="firstNameInput"
                            type="text"
                            placeholder={text('lblFirstName')}
                            onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                            autoFocus
                            formNoValidate
                        />
                    </InputGroup>
                    <InputGroup
                        style={{ width: '100%', height: '40px' }}
                        label={formData.lastName.length > 0 ? text('lblLastNameShortField') : ''}
                        errorTestId="signUpLastNameErrorMsg"
                        errorMsg={formErrors.lastName}
                        inverted={false}
                    >
                        <SInput
                            data-testid="lastNameInput"
                            type="text"
                            placeholder={text('lblLastNameShortField')}
                            onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                            formNoValidate
                        />
                    </InputGroup>
                </Row>
                <Row>
                    <InputGroup
                        style={{ width: '100%', height: '40px' }}
                        label={formData.email.length > 0 ? text('lblEmail') : ''}
                        errorTestId="signUpEmailErrorMsg"
                        errorMsg={formErrors.email}
                        inverted={false}
                    >
                        <SInput
                            data-testid="emailInput"
                            name="email"
                            type="email"
                            placeholder={text('lblEmail')}
                            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                            formNoValidate
                        />
                    </InputGroup>

                    <InputGroup
                        style={{ width: '100%', height: '40px' }}
                        label={formData.mobile.length > 0 ? text('lblContactNumberShortField') : ''}
                        errorTestId="signUpMobileErrorMsg"
                        errorMsg={formErrors.mobile}
                        inverted={false}
                    >
                        <SInput
                            data-testid="mobileInput"
                            type="text"
                            placeholder={text('lblContactNumberShortField')}
                            onChange={(e) => setFormData({ ...formData, mobile: e.target.value })}
                            formNoValidate
                        />
                    </InputGroup>
                </Row>
                <Row short>
                    <PasswordInput
                        style={{ width: '100%', height: '40px' }}
                        label={formData.password.length > 0 ? text('lblPassword') : ''}
                        errorTestId="signUpPasswordErrorMsg"
                        error={formErrors.password}
                        placeholder={text('lblPassword')}
                        showPassword={showPassword}
                        toggleShowPasswordFlag={toggleShowPassword}
                        onChange={(value) => setFormData({ ...formData, password: value })}
                        hasText={formData.password.length > 0}
                    />
                </Row>
                <AgreementContainer>
                    <Text
                        code="msgUserAgreementSignUpInfo"
                        values={{
                            link: {
                                href: `https://www.chargeamps.com/${text('linkLegal')}`,
                                target: '_new',
                            },
                            link2: {
                                href: `https://www.chargeamps.com/${text('linkPrivacyPolicy')}`,
                                target: '_new',
                            },
                        }}
                        data-testid="agreementText"
                    />
                </AgreementContainer>
                <div>
                    <SubmitButton disabled={loading} loading={loading}>
                        <Text code="btnSignUp" />
                    </SubmitButton>
                </div>
                <ReturnButton onClick={() => navigate('/login')} data-testid="goToSignIn">
                    <Text code="lblReturnToSignIn" />
                </ReturnButton>
            </Form>
        </ContentWrapper>
    )
}
